/**
 * AffiliateOversight - Admin Panel
 * Platform-wide affiliate and deep link management with abuse flagging
 * CSS prefix: aao- (admin affiliate oversight)
 */

import React, { useState, useEffect, useCallback } from 'react';
import './AffiliateOversight.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

/* =============================================
   SVG ICON COMPONENTS (Feather/Lucide style)
   ============================================= */

const Icons = {
  Users: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  UserCheck: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" />
    </svg>
  ),
  Flag: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  ),
  AlertTriangle: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  DollarSign: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  Link: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Search: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  RefreshCw: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  X: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  ChevronDown: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ChevronUp: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  ChevronLeft: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  ChevronRight: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  Shield: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  ShieldOff: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M19.69 14a6.9 6.9 0 0 0 .31-2V5l-8-3-3.16 1.18" /><path d="M4.73 4.73L4 5v7c0 6 8 10 8 10a20.29 20.29 0 0 0 5.62-4.38" /><line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  TrendingUp: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  BarChart: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  Clock: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  ExternalLink: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  CheckCircle: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  XCircle: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),
  Pause: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
    </svg>
  ),
  MousePointer: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /><path d="M13 13l6 6" />
    </svg>
  ),
  Inbox: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
};

/* =============================================
   UTILITY FUNCTIONS
   ============================================= */

const formatCurrency = (amount) => {
  const num = Number(amount) || 0;
  return '\u20A6' + num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatNumber = (num) => {
  return (Number(num) || 0).toLocaleString();
};

const getStatusBadgeClass = (status) => {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE') return 'aao-badge aao-badge-active';
  if (s === 'PAUSED') return 'aao-badge aao-badge-paused';
  if (s === 'DEACTIVATED' || s === 'SUSPENDED') return 'aao-badge aao-badge-deactivated';
  return 'aao-badge';
};

/* =============================================
   MAIN COMPONENT
   ============================================= */

const AffiliateOversight = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Toast
  const [toast, setToast] = useState(null);

  // Loading / Error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Overview data
  const [overview, setOverview] = useState(null);
  const [topEarners, setTopEarners] = useState([]);

  // Affiliates tab
  const [affiliates, setAffiliates] = useState([]);
  const [affiliatesTotal, setAffiliatesTotal] = useState(0);
  const [affiliatesPage, setAffiliatesPage] = useState(1);
  const [affiliatesSearch, setAffiliatesSearch] = useState('');
  const [affiliatesStatus, setAffiliatesStatus] = useState('');
  const [affiliatesFlagged, setAffiliatesFlagged] = useState(false);
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);
  const [affiliateDetail, setAffiliateDetail] = useState(null);

  // Deep Links tab
  const [deepLinks, setDeepLinks] = useState([]);
  const [deepLinksTotal, setDeepLinksTotal] = useState(0);
  const [deepLinksPage, setDeepLinksPage] = useState(1);
  const [deepLinksSearch, setDeepLinksSearch] = useState('');
  const [deepLinksFlagged, setDeepLinksFlagged] = useState(false);
  const [deepLinksHasAffiliate, setDeepLinksHasAffiliate] = useState('');

  // Flagged tab
  const [flaggedAffiliates, setFlaggedAffiliates] = useState([]);
  const [flaggedLinks, setFlaggedLinks] = useState([]);

  // Modals
  const [flagModal, setFlagModal] = useState(null); // { type: 'affiliate'|'deeplink', id, name }
  const [suspendModal, setSuspendModal] = useState(null); // { id, name }
  const [flagReason, setFlagReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');

  const LIMIT = 15;

  // ---- Toast helper ----
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ---- API helper ----
  const apiFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(`${API_BASE}${url}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || errData.error || `Request failed (${res.status})`);
    }
    return res.json();
  }, []);

  /* =============================================
     DATA FETCHING
     ============================================= */

  const fetchOverview = useCallback(async () => {
    try {
      const data = await apiFetch('/api/admin/affiliates/overview');
      const ov = data.overview || data;
      setOverview(ov);
      setTopEarners(ov.topEarners || []);
    } catch (err) {
      console.error('Failed to fetch overview:', err);
    }
  }, [apiFetch]);

  const fetchAffiliates = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
      });
      if (affiliatesSearch) params.set('search', affiliatesSearch);
      if (affiliatesStatus) params.set('status', affiliatesStatus);
      if (affiliatesFlagged) params.set('flagged', 'true');

      const data = await apiFetch(`/api/admin/affiliates/affiliates?${params}`);
      setAffiliates(data.affiliates || data.data || []);
      setAffiliatesTotal(data.total || data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to fetch affiliates:', err);
    }
  }, [apiFetch, affiliatesSearch, affiliatesStatus, affiliatesFlagged]);

  const fetchDeepLinks = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
      });
      if (deepLinksSearch) params.set('search', deepLinksSearch);
      if (deepLinksFlagged) params.set('flagged', 'true');
      if (deepLinksHasAffiliate) params.set('hasAffiliate', deepLinksHasAffiliate);

      const data = await apiFetch(`/api/admin/affiliates/deep-links?${params}`);
      setDeepLinks(data.deepLinks || data.data || []);
      setDeepLinksTotal(data.total || data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to fetch deep links:', err);
    }
  }, [apiFetch, deepLinksSearch, deepLinksFlagged, deepLinksHasAffiliate]);

  const fetchFlagged = useCallback(async () => {
    try {
      const [affData, linkData] = await Promise.all([
        apiFetch('/api/admin/affiliates/affiliates?flagged=true&limit=50'),
        apiFetch('/api/admin/affiliates/deep-links?flagged=true&limit=50'),
      ]);
      setFlaggedAffiliates(affData.affiliates || affData.data || []);
      setFlaggedLinks(linkData.deepLinks || linkData.data || []);
    } catch (err) {
      console.error('Failed to fetch flagged items:', err);
    }
  }, [apiFetch]);

  const fetchAffiliateDetail = useCallback(async (id) => {
    try {
      const data = await apiFetch(`/api/admin/affiliates/affiliates/${id}`);
      setAffiliateDetail(data.affiliate || data);
    } catch (err) {
      console.error('Failed to fetch affiliate detail:', err);
      showToast('error', 'Failed to load affiliate details');
    }
  }, [apiFetch, showToast]);

  // ---- Initial load ----
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchOverview(),
        fetchAffiliates(1),
        fetchDeepLinks(1),
        fetchFlagged(),
      ]);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fetchOverview, fetchAffiliates, fetchDeepLinks, fetchFlagged]);

  useEffect(() => {
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Tab-specific refetches ----
  useEffect(() => {
    if (!loading) {
      fetchAffiliates(affiliatesPage);
    }
  }, [affiliatesPage, affiliatesSearch, affiliatesStatus, affiliatesFlagged]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) {
      fetchDeepLinks(deepLinksPage);
    }
  }, [deepLinksPage, deepLinksSearch, deepLinksFlagged, deepLinksHasAffiliate]); // eslint-disable-line react-hooks/exhaustive-deps

  /* =============================================
     ACTIONS
     ============================================= */

  const handleFlagSubmit = async () => {
    if (!flagModal || !flagReason.trim()) {
      showToast('warning', 'Please provide a reason for flagging');
      return;
    }
    setActionLoading('flag');
    try {
      const endpoint = flagModal.type === 'affiliate'
        ? `/api/admin/affiliates/affiliates/${flagModal.id}/flag`
        : `/api/admin/affiliates/deep-links/${flagModal.id}/flag`;
      await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({ reason: flagReason.trim() }),
      });
      showToast('success', `${flagModal.type === 'affiliate' ? 'Affiliate' : 'Deep link'} flagged successfully`);
      setFlagModal(null);
      setFlagReason('');
      // Refresh relevant data
      fetchAffiliates(affiliatesPage);
      fetchDeepLinks(deepLinksPage);
      fetchFlagged();
      fetchOverview();
    } catch (err) {
      showToast('error', err.message || 'Failed to flag item');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendSubmit = async () => {
    if (!suspendModal) return;
    setActionLoading('suspend');
    try {
      await apiFetch(`/api/admin/affiliates/affiliates/${suspendModal.id}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ reason: suspendReason.trim() || undefined }),
      });
      showToast('success', 'Affiliate suspended successfully');
      setSuspendModal(null);
      setSuspendReason('');
      fetchAffiliates(affiliatesPage);
      fetchFlagged();
      fetchOverview();
    } catch (err) {
      showToast('error', err.message || 'Failed to suspend affiliate');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnflagAffiliate = async (id, restore = false) => {
    setActionLoading(`unflag-aff-${id}`);
    try {
      await apiFetch(`/api/admin/affiliates/affiliates/${id}/unflag`, {
        method: 'POST',
        body: JSON.stringify({ restore }),
      });
      showToast('success', restore ? 'Affiliate unflagged and restored' : 'Affiliate unflagged');
      fetchAffiliates(affiliatesPage);
      fetchFlagged();
      fetchOverview();
    } catch (err) {
      showToast('error', err.message || 'Failed to unflag affiliate');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnflagDeepLink = async (id) => {
    setActionLoading(`unflag-dl-${id}`);
    try {
      await apiFetch(`/api/admin/affiliates/deep-links/${id}/unflag`, {
        method: 'POST',
      });
      showToast('success', 'Deep link unflagged');
      fetchDeepLinks(deepLinksPage);
      fetchFlagged();
      fetchOverview();
    } catch (err) {
      showToast('error', err.message || 'Failed to unflag deep link');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExpandAffiliate = (id) => {
    if (expandedAffiliate === id) {
      setExpandedAffiliate(null);
      setAffiliateDetail(null);
    } else {
      setExpandedAffiliate(id);
      fetchAffiliateDetail(id);
    }
  };

  /* =============================================
     PAGINATION HELPER
     ============================================= */

  const renderPagination = (total, page, setPage) => {
    const totalPages = Math.ceil(total / LIMIT);
    if (totalPages <= 1) return null;

    const start = (page - 1) * LIMIT + 1;
    const end = Math.min(page * LIMIT, total);

    return (
      <div className="aao-pagination">
        <span className="aao-pagination-info">
          Showing {start}-{end} of {formatNumber(total)}
        </span>
        <div className="aao-pagination-btns">
          <button
            className="aao-page-btn"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            <Icons.ChevronLeft size={16} />
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (page <= 3) {
              pageNum = i + 1;
            } else if (page >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = page - 2 + i;
            }
            return (
              <button
                key={pageNum}
                className={`aao-page-btn ${page === pageNum ? 'aao-page-active' : ''}`}
                onClick={() => setPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            className="aao-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            <Icons.ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  /* =============================================
     OVERVIEW TAB
     ============================================= */

  const renderOverview = () => {
    const stats = overview || {};
    const statCards = [
      { label: 'Total Affiliates', value: formatNumber(stats.totalAffiliates), icon: <Icons.Users size={18} />, color: 'aao-purple' },
      { label: 'Active', value: formatNumber(stats.activeAffiliates), icon: <Icons.UserCheck size={18} />, color: 'aao-green' },
      { label: 'Flagged', value: formatNumber(stats.flaggedAffiliates), icon: <Icons.Flag size={18} />, color: 'aao-red' },
      { label: 'Suspended', value: formatNumber(stats.deactivatedAffiliates), icon: <Icons.ShieldOff size={18} />, color: 'aao-orange' },
      { label: 'Total Revenue', value: formatCurrency(stats.totalRevenue), icon: <Icons.TrendingUp size={18} />, color: 'aao-green' },
      { label: 'Total Commission', value: formatCurrency(stats.totalCommission), icon: <Icons.DollarSign size={18} />, color: 'aao-purple' },
      { label: 'Pending Commission', value: formatCurrency(stats.pendingCommission), icon: <Icons.Clock size={18} />, color: 'aao-orange' },
      { label: 'Flagged Deep Links', value: formatNumber(stats.flaggedDeepLinks), icon: <Icons.Link size={18} />, color: 'aao-red' },
    ];

    return (
      <div>
        <div className="aao-stats-grid">
          {statCards.map((card, i) => (
            <div key={i} className="aao-stat-card">
              <div className="aao-stat-header">
                <span className="aao-stat-label">{card.label}</span>
                <div className={`aao-stat-icon ${card.color}`}>{card.icon}</div>
              </div>
              <div className="aao-stat-value">{card.value}</div>
            </div>
          ))}
        </div>

        {/* Top Earners */}
        <div className="aao-card">
          <div className="aao-card-header">
            <h3 className="aao-card-title">
              <Icons.TrendingUp size={18} />
              Top Earners
            </h3>
          </div>
          <div className="aao-card-body">
            <div className="aao-table-wrap">
              <table className="aao-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Merchant / Store</th>
                    <th>Revenue</th>
                    <th>Commission</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {topEarners.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="aao-table-empty">
                        <Icons.Inbox size={32} />
                        <div>No top earners data available</div>
                      </td>
                    </tr>
                  ) : (
                    topEarners.map((earner, i) => (
                      <tr key={earner.id || i}>
                        <td><span className="aao-earners-rank">{i + 1}</span></td>
                        <td className="aao-table-name">{earner.name || earner.firstName + ' ' + earner.lastName}</td>
                        <td>
                          <div>{earner.merchantName || earner.storeName || '-'}</div>
                          {earner.storeSlug && <div className="aao-table-sub">{earner.storeSlug}</div>}
                        </td>
                        <td>{formatCurrency(earner.revenue || earner.totalRevenue)}</td>
                        <td>{formatCurrency(earner.commission || earner.totalCommission)}</td>
                        <td><span className={getStatusBadgeClass(earner.status)}>{earner.status || 'ACTIVE'}</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* =============================================
     AFFILIATES TAB
     ============================================= */

  const renderAffiliates = () => {
    return (
      <div className="aao-card">
        <div className="aao-toolbar">
          <div className="aao-search-wrap">
            <Icons.Search size={16} />
            <input
              type="text"
              className="aao-search-input"
              placeholder="Search affiliates by name or email..."
              value={affiliatesSearch}
              onChange={(e) => { setAffiliatesSearch(e.target.value); setAffiliatesPage(1); }}
            />
          </div>
          <select
            className="aao-filter-select"
            value={affiliatesStatus}
            onChange={(e) => { setAffiliatesStatus(e.target.value); setAffiliatesPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="DEACTIVATED">Deactivated</option>
          </select>
          <button
            className={`aao-filter-toggle ${affiliatesFlagged ? 'aao-active' : ''}`}
            onClick={() => { setAffiliatesFlagged(!affiliatesFlagged); setAffiliatesPage(1); }}
          >
            <Icons.Flag size={14} />
            Flagged Only
          </button>
        </div>

        <div className="aao-card-body">
          <div className="aao-table-wrap">
            <table className="aao-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Merchant / Store</th>
                  <th>Commission Rate</th>
                  <th>Revenue</th>
                  <th>Conversions</th>
                  <th>Status</th>
                  <th>Flagged</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="aao-table-empty">
                      <Icons.Inbox size={32} />
                      <div>No affiliates found</div>
                    </td>
                  </tr>
                ) : (
                  affiliates.map((aff) => (
                    <React.Fragment key={aff.id}>
                      <tr
                        className="aao-row-clickable"
                        onClick={() => handleExpandAffiliate(aff.id)}
                      >
                        <td className="aao-table-name">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {expandedAffiliate === aff.id ? <Icons.ChevronUp size={14} /> : <Icons.ChevronDown size={14} />}
                            {aff.name || `${aff.firstName || ''} ${aff.lastName || ''}`.trim() || '-'}
                          </div>
                        </td>
                        <td>{aff.email || '-'}</td>
                        <td>
                          <div>{aff.merchantName || aff.storeName || '-'}</div>
                          {aff.storeSlug && <div className="aao-table-sub">{aff.storeSlug}</div>}
                        </td>
                        <td>{aff.commissionRate != null ? `${aff.commissionRate}%` : '-'}</td>
                        <td>{formatCurrency(aff.revenue || aff.totalRevenue || 0)}</td>
                        <td>{formatNumber(aff.conversions || aff.totalConversions || 0)}</td>
                        <td><span className={getStatusBadgeClass(aff.status)}>{aff.status || 'ACTIVE'}</span></td>
                        <td>
                          {aff.flagged ? (
                            <span className="aao-badge-flagged">
                              <Icons.Flag size={12} />
                              Flagged
                            </span>
                          ) : '-'}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="aao-actions">
                            {!aff.flagged ? (
                              <button
                                className="aao-action-btn aao-action-flag"
                                title="Flag affiliate"
                                onClick={() => setFlagModal({ type: 'affiliate', id: aff.id, name: aff.name || aff.email })}
                              >
                                <Icons.Flag size={14} />
                              </button>
                            ) : (
                              <button
                                className="aao-action-btn aao-action-unflag"
                                title="Unflag affiliate"
                                disabled={actionLoading === `unflag-aff-${aff.id}`}
                                onClick={() => handleUnflagAffiliate(aff.id, true)}
                              >
                                <Icons.CheckCircle size={14} />
                              </button>
                            )}
                            <button
                              className="aao-action-btn aao-action-suspend"
                              title="Suspend affiliate"
                              onClick={() => setSuspendModal({ id: aff.id, name: aff.name || aff.email })}
                            >
                              <Icons.ShieldOff size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Inline detail panel */}
                      {expandedAffiliate === aff.id && (
                        <tr className="aao-detail-row">
                          <td colSpan="9">
                            <div className="aao-detail-panel">
                              {!affiliateDetail ? (
                                <div className="aao-loading" style={{ padding: '24px' }}>
                                  <div className="aao-spinner" />
                                  <span>Loading details...</span>
                                </div>
                              ) : (
                                <>
                                  <div className="aao-detail-grid">
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Full Name</span>
                                      <span className="aao-detail-value">
                                        {affiliateDetail.name || `${affiliateDetail.firstName || ''} ${affiliateDetail.lastName || ''}`.trim() || '-'}
                                      </span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Email</span>
                                      <span className="aao-detail-value">{affiliateDetail.email || '-'}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Phone</span>
                                      <span className="aao-detail-value">{affiliateDetail.phone || '-'}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Commission Rate</span>
                                      <span className="aao-detail-value">{affiliateDetail.commissionRate != null ? `${affiliateDetail.commissionRate}%` : '-'}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Total Revenue</span>
                                      <span className="aao-detail-value">{formatCurrency(affiliateDetail.totalRevenue || affiliateDetail.revenue || 0)}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Total Commission</span>
                                      <span className="aao-detail-value">{formatCurrency(affiliateDetail.totalCommission || affiliateDetail.commission || 0)}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Pending Commission</span>
                                      <span className="aao-detail-value">{formatCurrency(affiliateDetail.pendingCommission || 0)}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Total Clicks</span>
                                      <span className="aao-detail-value">{formatNumber(affiliateDetail.totalClicks || 0)}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Conversions</span>
                                      <span className="aao-detail-value">{formatNumber(affiliateDetail.totalConversions || affiliateDetail.conversions || 0)}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Deep Links</span>
                                      <span className="aao-detail-value">{formatNumber(affiliateDetail.deepLinksCount || 0)}</span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Joined</span>
                                      <span className="aao-detail-value">
                                        {affiliateDetail.createdAt ? new Date(affiliateDetail.createdAt).toLocaleDateString() : '-'}
                                      </span>
                                    </div>
                                    <div className="aao-detail-item">
                                      <span className="aao-detail-label">Status</span>
                                      <span className="aao-detail-value">
                                        <span className={getStatusBadgeClass(affiliateDetail.status)}>{affiliateDetail.status || 'ACTIVE'}</span>
                                      </span>
                                    </div>
                                  </div>
                                  {affiliateDetail.flagReason && (
                                    <div className="aao-warning-text" style={{ marginTop: '8px' }}>
                                      <Icons.Flag size={16} />
                                      <div>
                                        <strong>Flag reason:</strong> {affiliateDetail.flagReason}
                                      </div>
                                    </div>
                                  )}
                                  <div className="aao-detail-actions">
                                    {!affiliateDetail.flagged ? (
                                      <button
                                        className="aao-btn aao-btn-outline aao-btn-sm"
                                        onClick={() => setFlagModal({ type: 'affiliate', id: affiliateDetail.id, name: affiliateDetail.name || affiliateDetail.email })}
                                      >
                                        <Icons.Flag size={14} />
                                        Flag Affiliate
                                      </button>
                                    ) : (
                                      <button
                                        className="aao-btn aao-btn-success aao-btn-sm"
                                        disabled={actionLoading === `unflag-aff-${affiliateDetail.id}`}
                                        onClick={() => handleUnflagAffiliate(affiliateDetail.id, true)}
                                      >
                                        <Icons.CheckCircle size={14} />
                                        Unflag & Restore
                                      </button>
                                    )}
                                    <button
                                      className="aao-btn aao-btn-danger aao-btn-sm"
                                      onClick={() => setSuspendModal({ id: affiliateDetail.id, name: affiliateDetail.name || affiliateDetail.email })}
                                    >
                                      <Icons.ShieldOff size={14} />
                                      Suspend
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {renderPagination(affiliatesTotal, affiliatesPage, setAffiliatesPage)}
        </div>
      </div>
    );
  };

  /* =============================================
     DEEP LINKS TAB
     ============================================= */

  const renderDeepLinks = () => {
    return (
      <div className="aao-card">
        <div className="aao-toolbar">
          <div className="aao-search-wrap">
            <Icons.Search size={16} />
            <input
              type="text"
              className="aao-search-input"
              placeholder="Search deep links by title, URL, or short code..."
              value={deepLinksSearch}
              onChange={(e) => { setDeepLinksSearch(e.target.value); setDeepLinksPage(1); }}
            />
          </div>
          <select
            className="aao-filter-select"
            value={deepLinksHasAffiliate}
            onChange={(e) => { setDeepLinksHasAffiliate(e.target.value); setDeepLinksPage(1); }}
          >
            <option value="">All Links</option>
            <option value="true">With Affiliate</option>
            <option value="false">Without Affiliate</option>
          </select>
          <button
            className={`aao-filter-toggle ${deepLinksFlagged ? 'aao-active' : ''}`}
            onClick={() => { setDeepLinksFlagged(!deepLinksFlagged); setDeepLinksPage(1); }}
          >
            <Icons.Flag size={14} />
            Flagged Only
          </button>
        </div>

        <div className="aao-card-body">
          <div className="aao-table-wrap">
            <table className="aao-table">
              <thead>
                <tr>
                  <th>Title / URL</th>
                  <th>Short Code</th>
                  <th>Merchant</th>
                  <th>Affiliate</th>
                  <th>Clicks</th>
                  <th>Conversions</th>
                  <th>Revenue</th>
                  <th>Flagged</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deepLinks.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="aao-table-empty">
                      <Icons.Inbox size={32} />
                      <div>No deep links found</div>
                    </td>
                  </tr>
                ) : (
                  deepLinks.map((link) => (
                    <tr key={link.id}>
                      <td>
                        <div className="aao-table-name">{link.title || link.name || 'Untitled'}</div>
                        <div className="aao-table-sub" style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {link.originalUrl || link.url || '-'}
                        </div>
                      </td>
                      <td>
                        <code style={{ fontSize: '12px', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>
                          {link.shortCode || link.code || '-'}
                        </code>
                      </td>
                      <td>{link.merchantName || link.storeName || '-'}</td>
                      <td>{link.affiliateName || link.affiliate?.name || '-'}</td>
                      <td>{formatNumber(link.clicks || link.totalClicks || 0)}</td>
                      <td>{formatNumber(link.conversions || link.totalConversions || 0)}</td>
                      <td>{formatCurrency(link.revenue || link.totalRevenue || 0)}</td>
                      <td>
                        {link.flagged ? (
                          <span className="aao-badge-flagged">
                            <Icons.Flag size={12} />
                            Flagged
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        <div className="aao-actions">
                          {!link.flagged ? (
                            <button
                              className="aao-action-btn aao-action-flag"
                              title="Flag deep link"
                              onClick={() => setFlagModal({ type: 'deeplink', id: link.id, name: link.title || link.shortCode })}
                            >
                              <Icons.Flag size={14} />
                            </button>
                          ) : (
                            <button
                              className="aao-action-btn aao-action-unflag"
                              title="Unflag deep link"
                              disabled={actionLoading === `unflag-dl-${link.id}`}
                              onClick={() => handleUnflagDeepLink(link.id)}
                            >
                              <Icons.CheckCircle size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {renderPagination(deepLinksTotal, deepLinksPage, setDeepLinksPage)}
        </div>
      </div>
    );
  };

  /* =============================================
     FLAGGED TAB
     ============================================= */

  const renderFlagged = () => {
    const hasItems = flaggedAffiliates.length > 0 || flaggedLinks.length > 0;

    return (
      <div>
        {!hasItems && (
          <div className="aao-card">
            <div className="aao-card-body">
              <div className="aao-table-empty" style={{ padding: '60px 20px' }}>
                <Icons.CheckCircle size={40} />
                <div style={{ marginTop: '12px', fontSize: '15px', fontWeight: 500 }}>No flagged items</div>
                <div style={{ color: '#9ca3af', marginTop: '4px' }}>All affiliates and deep links are clean</div>
              </div>
            </div>
          </div>
        )}

        {/* Flagged Affiliates */}
        {flaggedAffiliates.length > 0 && (
          <div className="aao-flagged-section">
            <h3 className="aao-flagged-section-title">
              <Icons.Users size={18} />
              Flagged Affiliates ({flaggedAffiliates.length})
            </h3>
            <div className="aao-card">
              <div className="aao-card-body">
                <div className="aao-table-wrap">
                  <table className="aao-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Merchant</th>
                        <th>Revenue</th>
                        <th>Flag Reason</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flaggedAffiliates.map((aff) => (
                        <tr key={aff.id}>
                          <td className="aao-table-name">{aff.name || `${aff.firstName || ''} ${aff.lastName || ''}`.trim() || '-'}</td>
                          <td>{aff.email || '-'}</td>
                          <td>{aff.merchantName || aff.storeName || '-'}</td>
                          <td>{formatCurrency(aff.revenue || aff.totalRevenue || 0)}</td>
                          <td>
                            <span className="aao-flag-reason" title={aff.flagReason || ''}>
                              {aff.flagReason || '-'}
                            </span>
                          </td>
                          <td><span className={getStatusBadgeClass(aff.status)}>{aff.status || 'ACTIVE'}</span></td>
                          <td>
                            <div className="aao-actions">
                              <button
                                className="aao-btn aao-btn-danger aao-btn-sm"
                                disabled={actionLoading === `suspend-${aff.id}`}
                                onClick={() => setSuspendModal({ id: aff.id, name: aff.name || aff.email })}
                              >
                                <Icons.ShieldOff size={14} />
                                Suspend
                              </button>
                              <button
                                className="aao-btn aao-btn-success aao-btn-sm"
                                disabled={actionLoading === `unflag-aff-${aff.id}`}
                                onClick={() => handleUnflagAffiliate(aff.id, true)}
                              >
                                <Icons.CheckCircle size={14} />
                                Unflag
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Flagged Deep Links */}
        {flaggedLinks.length > 0 && (
          <div className="aao-flagged-section">
            <h3 className="aao-flagged-section-title">
              <Icons.Link size={18} />
              Flagged Deep Links ({flaggedLinks.length})
            </h3>
            <div className="aao-card">
              <div className="aao-card-body">
                <div className="aao-table-wrap">
                  <table className="aao-table">
                    <thead>
                      <tr>
                        <th>Title / URL</th>
                        <th>Short Code</th>
                        <th>Merchant</th>
                        <th>Affiliate</th>
                        <th>Flag Reason</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flaggedLinks.map((link) => (
                        <tr key={link.id}>
                          <td>
                            <div className="aao-table-name">{link.title || link.name || 'Untitled'}</div>
                            <div className="aao-table-sub" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {link.originalUrl || link.url || '-'}
                            </div>
                          </td>
                          <td>
                            <code style={{ fontSize: '12px', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>
                              {link.shortCode || link.code || '-'}
                            </code>
                          </td>
                          <td>{link.merchantName || link.storeName || '-'}</td>
                          <td>{link.affiliateName || link.affiliate?.name || '-'}</td>
                          <td>
                            <span className="aao-flag-reason" title={link.flagReason || ''}>
                              {link.flagReason || '-'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="aao-btn aao-btn-success aao-btn-sm"
                              disabled={actionLoading === `unflag-dl-${link.id}`}
                              onClick={() => handleUnflagDeepLink(link.id)}
                            >
                              <Icons.CheckCircle size={14} />
                              Unflag
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* =============================================
     MODALS
     ============================================= */

  const renderFlagModal = () => {
    if (!flagModal) return null;
    const isAffiliate = flagModal.type === 'affiliate';

    return (
      <div className="aao-modal-overlay" onClick={() => { setFlagModal(null); setFlagReason(''); }}>
        <div className="aao-modal" onClick={(e) => e.stopPropagation()}>
          <div className="aao-modal-header">
            <h3 className="aao-modal-title">
              <Icons.Flag size={18} style={{ color: '#dc2626' }} />
              Flag {isAffiliate ? 'Affiliate' : 'Deep Link'}
            </h3>
            <button className="aao-modal-close" onClick={() => { setFlagModal(null); setFlagReason(''); }}>
              <Icons.X size={18} />
            </button>
          </div>
          <div className="aao-modal-body">
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 16px' }}>
              Flagging <strong>{flagModal.name || 'this item'}</strong> for review. Please provide a reason below.
            </p>
            <div className="aao-form-group">
              <label className="aao-form-label">Reason for flagging</label>
              <textarea
                className="aao-form-textarea"
                placeholder="Describe the reason for flagging this item..."
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="aao-modal-footer">
            <button className="aao-btn aao-btn-outline" onClick={() => { setFlagModal(null); setFlagReason(''); }}>
              Cancel
            </button>
            <button
              className="aao-btn aao-btn-danger"
              disabled={!flagReason.trim() || actionLoading === 'flag'}
              onClick={handleFlagSubmit}
            >
              {actionLoading === 'flag' ? 'Flagging...' : 'Flag Item'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSuspendModal = () => {
    if (!suspendModal) return null;

    return (
      <div className="aao-modal-overlay" onClick={() => { setSuspendModal(null); setSuspendReason(''); }}>
        <div className="aao-modal" onClick={(e) => e.stopPropagation()}>
          <div className="aao-modal-header">
            <h3 className="aao-modal-title">
              <Icons.ShieldOff size={18} style={{ color: '#dc2626' }} />
              Suspend Affiliate
            </h3>
            <button className="aao-modal-close" onClick={() => { setSuspendModal(null); setSuspendReason(''); }}>
              <Icons.X size={18} />
            </button>
          </div>
          <div className="aao-modal-body">
            <div className="aao-warning-text">
              <Icons.AlertTriangle size={18} />
              <div>
                You are about to suspend affiliate <span className="aao-affiliate-name-highlight">{suspendModal.name || 'Unknown'}</span>.
                This will immediately deactivate their account and all associated deep links. This action can be reversed later.
              </div>
            </div>
            <div className="aao-form-group">
              <label className="aao-form-label">Reason for suspension (optional)</label>
              <textarea
                className="aao-form-textarea"
                placeholder="Provide a reason for the suspension..."
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
              />
            </div>
          </div>
          <div className="aao-modal-footer">
            <button className="aao-btn aao-btn-outline" onClick={() => { setSuspendModal(null); setSuspendReason(''); }}>
              Cancel
            </button>
            <button
              className="aao-btn aao-btn-danger"
              disabled={actionLoading === 'suspend'}
              onClick={handleSuspendSubmit}
            >
              {actionLoading === 'suspend' ? 'Suspending...' : 'Confirm Suspension'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* =============================================
     MAIN RENDER
     ============================================= */

  // Count flagged for badge
  const flaggedCount = (overview?.flaggedAffiliates || 0) + (overview?.flaggedDeepLinks || 0);

  if (loading) {
    return (
      <div className="aao-container">
        <div className="aao-loading">
          <div className="aao-spinner" />
          <span>Loading affiliate oversight...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aao-container">
        <div className="aao-error">
          <Icons.XCircle size={40} />
          <p>{error}</p>
          <button className="aao-retry-btn" onClick={loadAll}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="aao-container">
      {/* Toast */}
      {toast && (
        <div className={`aao-toast aao-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="aao-header">
        <div className="aao-header-left">
          <div className="aao-header-icon">
            <Icons.Users size={22} />
          </div>
          <div>
            <h1 className="aao-title">Affiliate Oversight</h1>
            <p className="aao-subtitle">Manage affiliates, deep links, and abuse flagging</p>
          </div>
        </div>
        <button
          className={`aao-refresh-btn ${loading ? 'aao-spinning' : ''}`}
          onClick={loadAll}
        >
          <Icons.RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="aao-tabs">
        <button
          className={`aao-tab ${activeTab === 'overview' ? 'aao-tab-active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <Icons.BarChart size={16} />
          Overview
        </button>
        <button
          className={`aao-tab ${activeTab === 'affiliates' ? 'aao-tab-active' : ''}`}
          onClick={() => setActiveTab('affiliates')}
        >
          <Icons.Users size={16} />
          Affiliates
        </button>
        <button
          className={`aao-tab ${activeTab === 'deeplinks' ? 'aao-tab-active' : ''}`}
          onClick={() => setActiveTab('deeplinks')}
        >
          <Icons.Link size={16} />
          Deep Links
        </button>
        <button
          className={`aao-tab ${activeTab === 'flagged' ? 'aao-tab-active' : ''}`}
          onClick={() => { setActiveTab('flagged'); fetchFlagged(); }}
        >
          <Icons.Flag size={16} />
          Flagged
          {flaggedCount > 0 && <span className="aao-tab-badge">{flaggedCount}</span>}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'affiliates' && renderAffiliates()}
      {activeTab === 'deeplinks' && renderDeepLinks()}
      {activeTab === 'flagged' && renderFlagged()}

      {/* Modals */}
      {renderFlagModal()}
      {renderSuspendModal()}
    </div>
  );
};

export default AffiliateOversight;
